import './Outreach.scss'
import React, { useEffect, useMemo, useState } from 'react'
import { ICustomer } from '@getgreenline/customers'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Col, Input, message, Row, Button } from 'antd'
import { SegmentedMessage } from 'sms-segments-calculator'
import { sendSms } from '@getgreenline/marketing'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { GrRegex, OutreachHelpers, parseErrorMsg } from '../../../../utilities/helpers'
import { OutreachRecipientsTable } from './OutreachRecipientsTable'
import { CustomTablePagination } from '../../../../components/CustomTablePagination'
import endpoints from '../../../../constants/Endpoints'
import classNames from 'classnames'
import { Alert } from 'antd-v4'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { InfoCircleFilled } from '@ant-design/icons'
import { THEME_COLORS } from '@getgreenline/shared'
interface Props {
  customers: ICustomer[]
  removeCustomerFromRecipientList: (customerId: string) => void
  clearListHandler: () => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const _ComposeMessage = ({
  customers,
  currentCompanyStore,
  currentUserStore,
  removeCustomerFromRecipientList,
  clearListHandler,
}: Props & InjectedProps) => {
  const outreachHelper = OutreachHelpers()

  const CHARACTER_LIMIT = 140
  const CHARACTER_ENCODING = 'GSM-7'

  const limit = 20
  const [offset, setOffset] = useState(0)
  const [customersPerPage, setCustomersPerPage] = useState<ICustomer[]>([])
  const [smsMessage, setMessage] = useState('')
  const [isSent, setIsSent] = useState(false)
  const [characterCount, setCharacterCount] = useState(0)
  const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(false)
  const [isExceedsCharLimit, setIsExceedsCharLimit] = useState(false)
  const [showInvalidPhoneBanner, setShowInvalidPhoneBanner] = useState(false)
  const [invalidPhoneNumbers, setInvalidPhoneNumbers] = useState(new Set<string>())
  const [numberOfRecipients, setNumberOfRecipients] = useState(customers.length)
  const tenantId = currentCompanyStore?.company?.id
  const userId = currentUserStore?.currentUser?.id
  if (!tenantId) throw new Error('Company not found, please refresh the page')
  if (!userId) throw new Error('User not found, please refresh the page')

  const hasPhone = (recipient: { phone: string | null }): recipient is { phone: string } =>
    !!recipient.phone

  const customerToRecipient = ({ phone, name }: ICustomer): { phone: ICustomer['phone'] } => {
    if (!phone) message.warn(`${name} has no phone number`)
    return { phone }
  }
  const recipients = customers.map(customerToRecipient).filter(hasPhone)

  const isDisabledSendButton = useMemo(() => {
    return (
      isSendButtonDisabled ||
      isExceedsCharLimit ||
      characterCount === 0 ||
      numberOfRecipients === 0 ||
      outreachHelper.hasInvalidWord(smsMessage)
    )
  }, [smsMessage])

  useEffect(() => {
    const invalidNumbers = new Set<string>()
    for (const recipient of recipients) {
      if (!isValidPhoneNumber(recipient.phone, 'CA')) {
        invalidNumbers.add(recipient.phone)
      } else {
        const number = parsePhoneNumber(recipient.phone, 'CA')
        if (number.country !== 'CA') {
          invalidNumbers.add(recipient.phone)
        }
      }
    }

    setInvalidPhoneNumbers(invalidNumbers)
  }, [customers])

  useEffect(() => {
    setCustomersPerPage(customers.slice(offset, offset + limit))
    setNumberOfRecipients(customers.length)
  }, [offset, customers])

  const handlePaginationClick = (offset: number) => {
    if (customers.length >= offset) {
      setOffset(offset)
    }
  }

  const removeCustomerFromRecipientListHandler = (recipientId: string) => {
    removeCustomerFromRecipientList(recipientId)
    if (isSendButtonDisabled) setIsSendButtonDisabled(false)
  }

  const sendSmsMessage = async () => {
    const containsInvalidPhoneNumbers = invalidPhoneNumbers.size > 0
    setShowInvalidPhoneBanner(containsInvalidPhoneNumbers)

    if (containsInvalidPhoneNumbers) return

    try {
      setIsSendButtonDisabled(true)
      if (!endpoints.AUTHZ_GATEWAY_URL) {
        throw new Error('AUTHZ_GATEWAY_URL is not configured for this environment')
      }
      await sendSms(endpoints.AUTHZ_GATEWAY_URL, `${tenantId}`, {
        userId,
        message: smsMessage,
        recipients,
      })
      message.success('Message successfully sent')
      setIsSent(true)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while sending the message')
    }
  }

  const limitMessageLength = (input: string) => {
    if (isSendButtonDisabled) setIsSendButtonDisabled(false)
    const cleanInput = GrRegex.cleanFrequentSpecialCharacters(input)
    const segmentedMessage = new SegmentedMessage(cleanInput)
    const messageLength = segmentedMessage.messageSize / 7 // calculate length from messageSize as numberOfCharacters attribute does not count 14 bit characters as 2.

    if (segmentedMessage.getEncodingName() === CHARACTER_ENCODING) {
      setMessage(cleanInput)
      setCharacterCount(messageLength)
      setIsExceedsCharLimit(messageLength > CHARACTER_LIMIT)
    }
  }

  return (
    <>
      <Row className='mb-3'>
        {showInvalidPhoneBanner && (
          <Alert
            showIcon
            type='error'
            message='Some customers have invalid phone numbers. Please remove them from the list to continue.'
          />
        )}
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12} md={{ span: 24 }} lg={{ span: 14 }}>
          {isSent ? (
            <div className='text-center'>
              Message successfully sent:
              <span
                className='mt-2'
                style={{ display: 'block', fontStyle: 'italic', whiteSpace: 'pre-line' }}
              >
                "{smsMessage}
                <br />
                <br />
                Text END to optout"
              </span>
            </div>
          ) : (
            <>
              <div className='compose-message'>
                <Input.TextArea
                  rows={4}
                  placeholder='Text message'
                  onChange={(e) => limitMessageLength(e.target.value)}
                  value={smsMessage}
                  allowClear
                  className={classNames({ 'compose-message__bad-input': isExceedsCharLimit })}
                />
                <small
                  className={classNames(
                    'float-left',
                    isExceedsCharLimit ? 'text-danger' : 'text-secondary',
                  )}
                >
                  {characterCount}/{CHARACTER_LIMIT}
                </small>
              </div>
              <div className='compose-message__action mt-4'>
                <span>
                  We will automatically add <strong>"Text END to optout"</strong> to the end of your
                  message
                </span>
                <BlazeButton
                  buttonProps={{
                    type: 'primary',
                    disabled: isDisabledSendButton,
                    onClick: sendSmsMessage,
                  }}
                  titleProps={{ title: 'Send' }}
                />
              </div>

              <Alert
                message={
                  <>
                    The use of words such as{' '}
                    <strong>"cannabis," "weed," "CBD," "pot," "kush," "vape,"</strong> and similar
                    terms is strictly prohibited.
                    <br />
                    <strong>
                      Messages containing these words will be blocked and cannot be sent.
                    </strong>
                    <br />
                    Please ensure your message complies with this policy to avoid any disruptions.
                  </>
                }
                type='warning'
                icon={<InfoCircleFilled />}
                showIcon
                className='mt-2'
              />

              {outreachHelper.hasInvalidWord(smsMessage) && (
                <>
                  <div className='mt-4'>Please remove the following words from your message:</div>
                  <div
                    contentEditable
                    style={{
                      marginTop: 8,
                      minHeight: 100,
                      padding: 8,
                      background: THEME_COLORS.MONO.monoFf,
                      border: `1px solid ${THEME_COLORS.MONO.monodd}`,
                      borderRadius: 4,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: outreachHelper.highlightInvalidWords(smsMessage),
                    }}
                  />
                </>
              )}
            </>
          )}
        </Col>
        <Col span={12} md={{ span: 24 }} lg={{ span: 10 }}>
          <CustomTablePagination
            onClick={handlePaginationClick}
            data={customersPerPage}
            showFirstPage
            hidePerPageOptions
            paginationPosition={'bottom'}
            pagination={{ offset, limit }}
            numberOfRows={customers.length}
          >
            <OutreachRecipientsTable
              removeCustomerFromRecipientList={removeCustomerFromRecipientListHandler}
              customers={customersPerPage}
              invalidPhoneNumbers={invalidPhoneNumbers}
              isLoading={false}
            />
            <small
              style={{ fontSize: '70%', borderRadius: '0' }}
              className='white-rounded-container border float-left mt-3'
            >
              {customers.length} contacts selected
            </small>
            <Button
              onClick={() => {
                clearListHandler()
                setOffset(0)
              }}
              className='float-left mt-3 ml-2'
            >
              Clear List
            </Button>
          </CustomTablePagination>
        </Col>
      </Row>
    </>
  )
}

export const ComposeMessage = inject(
  'currentCompanyStore',
  'currentUserStore',
)(observer(_ComposeMessage))
